﻿import type { GetMQTTResponse } from '@/api/mqtt.api'

import { getMqttValue } from './deviceTemplate'

export type DevicePowerState = 'on' | 'off' | 'unconfigured'

/** Mapowanie statusu przyjmowane zarówno z rekordu szablonu, jak i z draftu
 * edytora. `deviceBitIndex` jest tu jawnie nullowalny, bo DTO deklaruje `number`,
 * a draft legalnie trzyma `null`, wybierając odczyt bezpośredni. */
export type DeviceStatusMapping = {
  deviceStatusDataType?: string | null
  deviceStatusIndex?: number | null
  deviceBitIndex?: number | null
}

export type ConfiguredDeviceStatusMapping = {
  deviceStatusDataType: string
  deviceStatusIndex: number
  deviceBitIndex?: number | null
}

/** Kompletne mapowanie statusu urządzenia. Do odczytu bezpośredniego wystarczą
 * typ danych i główny indeks. `deviceBitIndex: null | undefined` świadomie
 * wybiera ten tryb, natomiast `0` pozostaje poprawnym indeksem pierwszego bitu. */
export const hasDeviceStatusMapping = (
  mapping: DeviceStatusMapping | undefined,
): mapping is ConfiguredDeviceStatusMapping =>
  typeof mapping?.deviceStatusDataType === 'string' &&
  mapping.deviceStatusDataType.trim().length > 0 &&
  typeof mapping.deviceStatusIndex === 'number' &&
  Number.isInteger(mapping.deviceStatusIndex) &&
  mapping.deviceStatusIndex >= 0

const validBitIndex = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 15

export const devicePowerStateOf = (
  mapping: DeviceStatusMapping | undefined,
  mqtt: GetMQTTResponse[],
): DevicePowerState => {
  if (!hasDeviceStatusMapping(mapping)) return 'unconfigured'
  if (
    mapping.deviceBitIndex !== null &&
    mapping.deviceBitIndex !== undefined &&
    !validBitIndex(mapping.deviceBitIndex)
  ) {
    return 'off'
  }

  const value = getMqttValue(
    mqtt,
    mapping.deviceStatusDataType.trim(),
    mapping.deviceStatusIndex,
    mapping.deviceBitIndex ?? undefined,
  )

  return value !== 0 ? 'on' : 'off'
}
